import type { AppType, AuthMethod } from "../api/types";
import { hasSdk } from "./clientTypes";

// The prompt a user pastes into their AI model to wire a client up to their
// code, whichever tool they use and whichever language the project is in. It
// is a thin pointer on purpose: with an SDK for the client's stack, the
// Intility ID MCP server holds the setup guidance in its sdk-integration
// skill, so instructions spelled out here would go stale the moment the
// skills update. Without one, the prompt points at the realm's standard
// OpenID Connect endpoints instead, and the agent builds the configuration
// itself. The prompt carries only what the portal knows and no skill or
// discovery document does: the client and its environment, the branch on
// whether the MCP server is connected, and the handoff of a SPA's redirect
// URL into the portal.

export interface WireUpClient {
  name: string;
  clientId: string;
  appType: AppType;
  authMethod: AuthMethod | null;
  redirectUris: string[];
  /** The stack recorded for the client's application (ADR-0060), or null. */
  stack: string | null;
  /** The realm the client was registered in. */
  environment: "development" | "production";
  /** The portal's page on connecting the MCP server, for an agent without it. */
  mcpDocsUrl: string;
  /** The authentication SDK reference, for an agent that cannot connect the MCP server at all. */
  sdkDocsUrl: string;
  /** The development and production page, for the full environment table. */
  environmentsDocsUrl: string;
}

const TYPE_LABEL: Record<AppType, string> = {
  spa: "single-page app (SPA)",
  api: "API",
  worker: "outbound-only service (worker)",
  custom: "custom client",
};

// The hosted MCP server per environment: an agent without it connects the
// one for the client's environment. Keep in step with
// docs/getting-started/environments.md.
const MCP_SERVER_URL: Record<WireUpClient["environment"], string> = {
  development: "https://mcp.intility-id-nxa5h0.intilitycloud.com/mcp",
  production: "https://mcp.apps.intility-id-65mfd0.clusters.zone-1.xcv.net/mcp",
};

// The Keycloak realm per environment: the authority every token comes from.
// Keep in step with docs/getting-started/environments.md.
export const REALM_URL: Record<WireUpClient["environment"], string> = {
  development: "https://login-dev.intility.com/realms/intility",
  production: "https://login.intility.com/realms/intility",
};

// How a client authenticates, as a fact in the facts block. The skill picks
// the exact calls from it; the prompt does not spell them out.
function credentialLine(client: WireUpClient): string | null {
  if (client.authMethod === "spiffe") {
    return "Credential: its SPIFFE workload identity (no secret is used)";
  }
  if (client.authMethod === "secret") {
    return "Credential: a client secret (I fetch it from this client's page in the Intility ID portal)";
  }
  return null;
}

// Step 2, with the server connected: read the skill and follow it. The lane
// is the skill's walk-through for the client's kind. A custom client has
// none, because its configuration is non-standard.
function skillSteps(client: WireUpClient): string {
  const lane =
    client.appType === "custom"
      ? "A custom client has a non-standard configuration that the Intility ID team manages, so the skill may not cover it: ask me before you invent a setup."
      : `Follow its ${client.appType} lane for this project's language and framework: the skill has the exact packages, calls and option names. Do not guess an API, and ask me if it does not cover something.`;
  return `If it is connected: make sure it speaks to the ${client.environment} environment (its instructions say which one it speaks to; development and production are separate installations, and this client exists only in ${client.environment}). Then read its skill "sdk-integration" with id_get_skill, and the references it names with id_get_skill_file. ${lane}`;
}

// Step 3, without the server: how the agent gets it.
function connectSteps(client: WireUpClient): string {
  return `If it is not connected: connect it before you write any code. The server for this environment is ${MCP_SERVER_URL[client.environment]}, and how to add an MCP server to each tool is at ${client.mcpDocsUrl}. If you cannot connect it yourself, give me that URL and ask me to connect it and restart you, then continue with this prompt. If it cannot be connected at all, read the SDK documentation at ${client.sdkDocsUrl} and follow it instead. Do not guess an API.`;
}

// The no-SDK opening: why the agent builds the configuration itself. The
// recorded stack says so; with none recorded, the agent works the stack out
// and stops if it turns out to be one an SDK covers.
function noSdkSteps(client: WireUpClient): string {
  return client.stack
    ? `Intility ID has an SDK for React (a SPA's login) and .NET (APIs and other services) only. The stack recorded for this client is ${client.stack}, and no SDK covers it, so build the configuration yourself with standard OAuth 2.0 and OpenID Connect. Do not look for or invent an Intility ID SDK for this stack.`
    : `Intility ID has an SDK for React (a SPA's login) and .NET (APIs and other services) only, and no stack is recorded for this client: work it out from the project's files. If the project uses one of those stacks, tell me before you build anything, because an SDK exists that does this work. Otherwise build the configuration yourself with standard OAuth 2.0 and OpenID Connect.`;
}

// The endpoints of the realm, the machine-readable way: the discovery
// document. The environments page holds the rest of the table.
function realmSteps(client: WireUpClient): string {
  const realm = REALM_URL[client.environment];
  return `The authority is the Keycloak realm for this environment: ${realm}. Read its discovery document, ${realm}/.well-known/openid-configuration, for the endpoints: the issuer, the authorization and token endpoints, and the JSON web key set (JWKS) that signs the tokens. The rest of the environment table is at ${client.environmentsDocsUrl}. A token from the other environment is never valid here.`;
}

// What building the configuration itself means, per kind.
function buildLane(client: WireUpClient): string {
  switch (client.appType) {
    case "spa":
      return "This client is public: sign the user in with the authorization code flow and PKCE, with no client secret. Keep the tokens in memory, and send them as a bearer token when the app calls an API.";
    case "api":
      return `This API validates the bearer token on each request it receives: the issuer is the realm above, the signing keys are the realm's JSON web key set, and the audience is this API's client ID (${client.clientId}). Reject a token whose issuer is any other realm.`;
    default:
      return `This service acts as itself: get a token with the OAuth client credentials grant at the realm's token endpoint, and send it as a bearer token on calls to other services. The scope names the target API's client ID.${client.authMethod === "spiffe" ? " Its credential is a SPIFFE workload identity: the pod presents its SVID as a client assertion on the token request, an exchange the .NET SDK does. Without an SDK, confirm the plan with me before you build that exchange by hand." : ""}`;
  }
}

// A SPA's login also needs a redirect URL registered on the client, and only
// the portal or the MCP server can register it. With the server, the agent
// registers the URL itself; without it, the URL comes back to the user, to
// paste into the "Wire up login" box: that box is the portal's half of the
// handoff.
function redirectUrlSteps(client: WireUpClient): string {
  return `The login also needs a redirect URL registered on the client: the address it sends the user back to, compared literally, so the login fails without any error if this project's address is not registered. Find this project's dev server address (vite.config, package.json or the framework config) and build the redirect URL from it: the address with a wildcard on the end, for example http://localhost:5173/*. ${
    client.redirectUris.length > 0
      ? `Already registered on the client: ${client.redirectUris.join(", ")}. If the address you found is covered by one of them, say so.`
      : "No redirect URL is registered on the client yet."
  } If you have the Intility ID MCP server, register the URL yourself: find this client with id_list_clients and add the URL with id_add_redirect_uri. If you do not have it, you cannot register the URL yourself, and your LAST message to me must give me the redirect URL on a line of its own, with only the URL on that line (no quotes, backticks or label), and tell me to paste it into the "Wire up login" box on this client in the Intility ID portal to finish the wire up. Do not skip it, even if everything else went well.`;
}

export function buildWireUpPrompt(client: WireUpClient): string {
  // Login only for a SPA: every other kind wires up authentication.
  const isSpa = client.appType === "spa";
  const credential = credentialLine(client);
  // A custom client keeps the MCP path whatever its stack: its configuration
  // is non-standard, and the skill steps already tell the agent to ask
  // before it invents a setup. Every other client without an SDK for its
  // stack builds the configuration itself.
  const buildYourself =
    client.appType !== "custom" && !hasSdk(client.stack, client.appType);
  return `Wire up Intility ID ${isSpa ? "login" : "authentication"} in this project.

First, work out which AI tool or coding agent you are running in (for example a terminal coding agent, an editor assistant such as Cursor or GitHub Copilot, or a plain chat window) and prepare accordingly: use that tool's own way to read and edit files, run commands and ask me questions, and follow its project conventions. Do not assume abilities you do not have. If you cannot edit files or run commands here, give me the exact changes as numbered steps and snippets that I can apply myself.

Client: ${client.name}
Kind: ${TYPE_LABEL[client.appType] ?? client.appType}
Client ID: ${client.clientId}
Environment: ${client.environment}${client.stack ? `\nStack: ${client.stack}` : ""}${credential ? `\n${credential}` : ""}

Work out this project's language and framework from its files (package.json, a .csproj file, and the like) before you start${client.stack ? `; the stack recorded for this client says ${client.stack}, so confirm the files agree with it` : ""}. If you cannot tell, ask me.

${
  buildYourself
    ? `${noSdkSteps(client)}

${realmSteps(client)}

${buildLane(client)}`
    : `The Intility ID MCP server holds the guidance for this task. Do this, in order:

1. Check whether it is connected to you: look for a server named intility-id, or for tools whose names start with id_ (for example id_list_skills).
2. ${skillSteps(client)}
3. ${connectSteps(client)}`
}${isSpa ? `\n${redirectUrlSteps(client)}\n` : ""}
Rules:
- Never write a token or a secret into a file in the repo, print one, or commit one.
- Use fixed package versions.
- Run the project's build or type-check when you are done and fix what it reports.
- Finish with a short summary of what you changed and anything I still have to do myself${isSpa ? ", such as pasting a redirect URL into the Intility ID portal" : ""}.`;
}
